import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";

// Redux
import { registerUser } from "../../store/actions/authSlice";
import { getTerms, getPrivacy, recordConsent } from "../../api/legal";

// App Components
import Copyright from "../copyrightFooter";
import LoadingBox from "../indicators/LoadingBox";

// Material UI
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import InputAdornment from "@mui/material/InputAdornment";
import FormControlLabel from "@mui/material/FormControlLabel";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BadgeIcon from "@mui/icons-material/Badge";

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const riseUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 200, damping: 24 },
  },
};

function Signup() {
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const auth = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    allowExtraEmails: false,
    acceptTerms: false,
  });

  const [legalDocs, setLegalDocs] = useState({ terms: null, privacy: null });

  useEffect(() => {
    if (auth.auth?.isLoggedIn) {
      history.push("/central-delivery");
    }
  }, [auth.auth, history]);

  useEffect(() => {
    Promise.all([getTerms(), getPrivacy()])
      .then(([termsRes, privacyRes]) => {
        setLegalDocs({ terms: termsRes.data, privacy: privacyRes.data });
      })
      .catch(() => {
        /* non-blocking: signup still works if docs endpoint is down */
      });
  }, []);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);
  const handleMouseDownPassword = (e) => e.preventDefault();

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setOpenErrorAlert(false);

    try {
      if (
        !formData.username ||
        !formData.email ||
        !formData.password ||
        !formData.confirmPassword ||
        !formData.firstName ||
        !formData.lastName
      ) {
        setErrorMessage("Por favor, preencha todos os campos obrigatorios.");
        setOpenErrorAlert(true);
        setLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setErrorMessage("As senhas nao coincidem.");
        setOpenErrorAlert(true);
        setLoading(false);
        return;
      }

      if (!formData.acceptTerms) {
        setErrorMessage(
          "Você precisa aceitar os Termos de Uso e a Política de Privacidade para continuar."
        );
        setOpenErrorAlert(true);
        setLoading(false);
        return;
      }

      const result = await dispatch(
        registerUser({
          username: formData.username,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          password: formData.password,
        })
      );

      if (result.error) {
        throw new Error(result.error.message || "Falha no cadastro");
      }

      try {
        const consentCalls = [];
        if (legalDocs.terms?.document_id) {
          consentCalls.push(recordConsent(legalDocs.terms.document_id));
        }
        if (legalDocs.privacy?.document_id) {
          consentCalls.push(recordConsent(legalDocs.privacy.document_id));
        }
        if (consentCalls.length) {
          await Promise.allSettled(consentCalls);
        }
      } catch (_) {
        /* consent recording is best-effort; do not block signup */
      }

      history.push("/login");
    } catch (error) {
      console.error(error);
      const message =
        error.message || "Erro ao realizar cadastro. Tente novamente.";
      setErrorMessage(message);
      setOpenErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
        }}
      >
        {/* ─── HERO ─── */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            pt: { xs: 6, md: 9 },
            pb: { xs: 12, md: 16 },
            background:
              "linear-gradient(155deg, #0f172a 0%, #1e293b 35%, #1e3a5f 70%, #1e3a8a 100%)",
          }}
        >
          {/* Grid pattern */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.03,
              backgroundImage:
                "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
              backgroundSize: "64px 64px",
              zIndex: 0,
            }}
          />

          {/* Glow orbs */}
          <Box
            component={motion.div}
            animate={{ x: [0, 30, 0], y: [0, -15, 0] }}
            transition={{
              duration: 14,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            sx={{
              position: "absolute",
              width: 500,
              height: 500,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 65%)",
              top: -120,
              right: -80,
              zIndex: 0,
            }}
          />
          <Box
            component={motion.div}
            animate={{ x: [0, -20, 0], y: [0, 20, 0] }}
            transition={{
              duration: 18,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            sx={{
              position: "absolute",
              width: 350,
              height: 350,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 65%)",
              bottom: -100,
              left: -50,
              zIndex: 0,
            }}
          />

          {/* Angled separator */}
          <Box
            sx={{
              position: "absolute",
              bottom: -1,
              left: 0,
              right: 0,
              lineHeight: 0,
            }}
          >
            <svg
              viewBox="0 0 1440 80"
              preserveAspectRatio="none"
              style={{ width: "100%", height: 50, display: "block" }}
            >
              <path
                d="M0 80L1440 0V80H0Z"
                fill={theme.palette.background.default}
              />
            </svg>
          </Box>
        </Box>

        {/* ─── FORM CARD ─── */}
        <Container
          maxWidth="sm"
          sx={{
            mt: { xs: -6, md: -10 },
            pb: 8,
            position: "relative",
            zIndex: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            }}
          >
            {/* Accent bar */}
            <Box
              sx={{
                height: 4,
                background: "linear-gradient(90deg, #10b981 0%, #2563eb 100%)",
              }}
            />

            <Box sx={{ p: { xs: 3, md: 4 } }}>
              {/* Logo */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Box
                  component="img"
                  src="/images/logo.png"
                  alt="Slapie"
                  sx={{
                    width: { xs: 72, md: 88 },
                    height: { xs: 72, md: 88 },
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: `3px solid ${theme.palette.divider}`,
                  }}
                />
              </Box>
              {loading ? (
                <Box sx={{ py: 6 }}>
                  <LoadingBox heightMetric="100px" />
                  <Typography
                    align="center"
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    Criando sua conta...
                  </Typography>
                </Box>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Form header */}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <BadgeIcon sx={{ color: "#10b981" }} />
                    <Typography variant="h6" fontWeight={600}>
                      Seus dados
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Preencha as informacoes abaixo para criar sua conta.
                  </Typography>

                  <Box component="form" noValidate onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          autoComplete="given-name"
                          name="firstName"
                          required
                          fullWidth
                          id="firstName"
                          label="Primeiro Nome"
                          autoFocus
                          size="small"
                          value={formData.firstName}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          id="lastName"
                          label="Sobrenome"
                          name="lastName"
                          autoComplete="family-name"
                          size="small"
                          value={formData.lastName}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          id="username"
                          label="Usuario"
                          name="username"
                          autoComplete="username"
                          size="small"
                          value={formData.username}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          id="email"
                          label="Email"
                          name="email"
                          autoComplete="email"
                          size="small"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          name="password"
                          label="Senha"
                          type={showPassword ? "text" : "password"}
                          id="password"
                          autoComplete="new-password"
                          size="small"
                          value={formData.password}
                          onChange={handleChange}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPassword}
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                  size="small"
                                >
                                  {showPassword ? (
                                    <VisibilityIcon fontSize="small" />
                                  ) : (
                                    <VisibilityOffIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          name="confirmPassword"
                          label="Confirmar Senha"
                          type={showConfirmPassword ? "text" : "password"}
                          id="confirmPassword"
                          autoComplete="new-password"
                          size="small"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle confirm password visibility"
                                  onClick={handleClickShowConfirmPassword}
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                  size="small"
                                >
                                  {showConfirmPassword ? (
                                    <VisibilityIcon fontSize="small" />
                                  ) : (
                                    <VisibilityOffIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="allowExtraEmails"
                              checked={formData.allowExtraEmails}
                              onChange={handleChange}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" color="text.secondary">
                              Receber novidades e artigos do Slapie
                            </Typography>
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="acceptTerms"
                              checked={formData.acceptTerms}
                              onChange={handleChange}
                              color="primary"
                              size="small"
                              required
                            />
                          }
                          label={
                            <Typography variant="body2" color="text.secondary">
                              Li e aceito os{" "}
                              <Link
                                href="/termos"
                                target="_blank"
                                rel="noopener"
                                underline="hover"
                                sx={{ fontWeight: 600 }}
                              >
                                Termos de Uso
                              </Link>{" "}
                              e a{" "}
                              <Link
                                href="/politica-privacidade"
                                target="_blank"
                                rel="noopener"
                                underline="hover"
                                sx={{ fontWeight: 600 }}
                              >
                                Política de Privacidade
                              </Link>
                              .
                            </Typography>
                          }
                        />
                      </Grid>
                    </Grid>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      endIcon={<PersonAddIcon />}
                      sx={{
                        mt: 3,
                        mb: 1,
                        py: 1.3,
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        background:
                          "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        boxShadow: "0 4px 16px rgba(16,185,129,0.3)",
                        "&:hover": {
                          boxShadow: "0 8px 24px rgba(16,185,129,0.4)",
                        },
                      }}
                    >
                      Cadastrar
                    </Button>

                    {/* Error Alert */}
                    {openErrorAlert && (
                      <Alert
                        variant="filled"
                        severity="error"
                        action={
                          <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => setOpenErrorAlert(false)}
                          >
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                        }
                        sx={{ mt: 2, borderRadius: "8px" }}
                      >
                        {errorMessage}
                      </Alert>
                    )}

                    {/* Link to login */}
                    <Box
                      sx={{
                        mt: 3,
                        pt: 2.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        Ja tem uma conta?
                      </Typography>
                      <Link
                        href="/login"
                        variant="body2"
                        underline="hover"
                        onClick={(e) => {
                          e.preventDefault();
                          history.push("/login");
                        }}
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                        }}
                      >
                        Faca o login
                      </Link>
                    </Box>
                  </Box>
                </motion.div>
              )}
            </Box>
          </Paper>

          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            display="block"
            sx={{ mt: 2 }}
          >
            Seus dados estao protegidos conforme a LGPD (Lei 13.709/2018)
          </Typography>

          <Copyright sx={{ mt: 2 }} />
        </Container>
      </Box>
    </motion.div>
  );
}

export default Signup;
